'use client';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { MarketoForms2 } from '@/app/[locale]/_components/third-party-scripts/MarketoForms2';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { getMarketoPrefillValues } from '@/lib/marketo-form-prefill';
import { cn } from '@/lib/styles';
import './MarketoForm.css';

// Non-critical: only loads when a RevenueHero router is configured
const RevenueHeroScheduler = dynamic(() => import('@/app/[locale]/_components/third-party-scripts/RevenueHeroScheduler').then(m => m.RevenueHeroScheduler));
type MarketoFormInstance = {
  vals: (values: Record<string, string>) => void;
};
type MktoForms2 = {
  loadForm: (baseUrl: string, munchkinId: string, formId: string | number, callback?: (form: MarketoFormInstance) => void) => void;
  whenReady: (callback: (form: MarketoFormInstance) => void) => void;
};
type MarketoFormProps = {
  id: number;
  loadingSkeleton?: React.ReactNode;
  rootMargin?: string;
  className?: string;
  // when set, loads RevenueHero and binds its scheduler to this form
  revenueHeroRouterId?: string;
  // above-the-fold placements (e.g. hero): load eagerly with afterInteractive instead of on-scroll/idle
  priority?: boolean;
};
const ROOT_MARGIN = '200px';
export const MarketoForm = ({
  id,
  loadingSkeleton,
  rootMargin = ROOT_MARGIN,
  className,
  revenueHeroRouterId,
  priority = false
}: MarketoFormProps) => {
  const formLoadedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Eager placements: skip the IntersectionObserver gate entirely
  const [isVisible, setIsVisible] = useState(priority);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const t = useTranslations('MarketoForm');
  const formId = `mktoForm_${id}`;

  // Load form when near the viewport (skipped for eager/priority placements)
  useEffect(() => {
    if (priority) return;
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin,
      threshold: 0
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [rootMargin, priority]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: The id is not expected to change
  useEffect(() => {
    if (!isVisible || formLoadedRef.current) return;
    let attempts = 0;
    const load = () => {
      try {
        // Poll for MktoForms2 to be available
        if (!('MktoForms2' in global)) {
          if (attempts++ < 100) return setTimeout(load, 100);
          throw new Error('MktoForms2 not defined in global');
        }
        const marketo = global.MktoForms2 as MktoForms2;
        marketo.whenReady(() => {
          setIsLoading(false);
          setHasError(false);
        });

        // Prefill in the per-form callback: whenReady would re-apply on every later registration
        marketo.loadForm('https://learn.lokalise.com', '936-ZMW-404', id, form => {
          const prefillValues = getMarketoPrefillValues(window.location.search);
          if (Object.keys(prefillValues).length > 0) {
            form.vals(prefillValues);
          }
        });
        formLoadedRef.current = true;
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: error logging for form load failure
        console.error('Failed to load Marketo form:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };
    load();
  }, [isVisible]);
  return <div ref={containerRef}>
			{/* Only load Marketo script when form becomes visible */}
			{isVisible && <MarketoForms2 strategy={priority ? 'afterInteractive' : 'lazyOnload'} />}

			{/* Only load RevenueHero when a router is configured and the form is visible */}
			{isVisible && revenueHeroRouterId && <RevenueHeroScheduler routerId={revenueHeroRouterId} marketoFormId={id} />}

			{hasError ? <Alert variant="destructive">
					<ExclamationTriangleIcon className="size-4" />
					<AlertTitle>{t('loadingError', {
          id: String(id)
        })}</AlertTitle>
				</Alert> : <div className="grid">
					{/* Skeleton and form occupy the same grid cell - skeleton hides instantly when form is loaded */}
					<div className={cn('col-start-1 row-start-1', {
        hidden: !isLoading
      })}>
						{loadingSkeleton}
					</div>

					{/* Only render the form element when visible to prevent unnecessary DOM mutations */}
					{isVisible && <form id={formId} className={cn('col-start-1 row-start-1', className)} />}
				</div>}
		</div>;
};